import _startCase from "lodash-es/startCase";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { useDatabasesNames } from "services/databases/useDatabasesNames";

import { useMenu } from "components/ActionMenu/useMenu";
import { Menu } from "components/LeftSidebar/RecordMenu/RecordMenu";

import { ROLES_WIZARD_ADD_GROUP_EMPTY } from "../privilegesReducer";
import { tablePrivileges } from "../types";
import { usePrivilegesState } from "../usePrivilegesContext";
import { getRenderDatabaseItem } from "./DatabaseItems";
import { GrantPrivilegesSelector } from "./GrantPrivilegesSelector";
import { GroupInner } from "./GroupInner";
import { TablesItems } from "./TablesItems";
import { usePrivilegesHandlers } from "./usePrivilegesHandlers";

import styles from "./styles.module.scss";

type Props = {
  index: number;
  resource: string;
};

export const TablesGroup = (props: Props) => {
  const { index, resource } = props;
  const { privilegesState, privilegesDispatch } = usePrivilegesState();

  const { onGrantPrivileges, deleteGroup, handleMore } = usePrivilegesHandlers({
    index,
    resource,
  });

  const groupState = privilegesState.table[index];
  const { objects, expanded } = groupState;

  const menu = useMenu();
  const databases = useDatabasesNames();
  const popoverRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const menuItems = [
    [
      {
        key: "add_group",
        text: t("roles_wizard.privileges.menu.add"),
        action: () => {
          privilegesDispatch({
            type: ROLES_WIZARD_ADD_GROUP_EMPTY,
            resource,
            objects: [],
          });
        },
        testId: "roles-wizard-add-tables-group",
      },
    ],

    ...(privilegesState.table.length > 1
      ? [
          [
            {
              key: "delete",
              text: t("roles_wizard.privileges.menu.delete"),
              action: deleteGroup,
              testId: "roles-wizard-delete-tables-group",
            },
          ],
        ]
      : []),
  ];

  const privilegesOptions = tablePrivileges.map(privilege => {
    return {
      value: privilege,
      text: _startCase(privilege.toLowerCase()),
    };
  });

  const renderItem = getRenderDatabaseItem({
    databases,
    popoverRef,
    renderChildren: (catalogName: string) => (
      <TablesItems
        catalogName={catalogName}
        index={index}
        resource={resource}
      />
    ),
  });

  return (
    <div className={styles.group}>
      <GroupInner
        handleMore={handleMore}
        expanded={expanded}
        objects={objects}
        items={databases}
        renderItem={renderItem}
        resource={resource}
        index={index}
        searchPlaceholder={t("roles_wizard.privileges.search_table")}
        searchKey="catalogName"
      />
      <GrantPrivilegesSelector
        groupState={groupState}
        onChange={onGrantPrivileges}
        privileges={privilegesOptions}
        resource={resource}
        index={index}
      />
      <Menu
        items={menuItems}
        menu={menu}
        testId={`roles-wizard-${resource}-${index}-menu`}
        alwaysVisible
      />
    </div>
  );
};
